import { useNewSlidesStore } from "@editor/modules/editor/stores/use-new-slides-store";
import { store, useAppDispatch } from "@store";
import { setContentSlide } from "@store/ai-slides";
import { getThumbnailId } from "@store/story/selectors";
import { useCallback } from "react";

/**
 * Content-bearing library inserts (saved slides, theme/template slides) are
 * meant to REPLACE a freshly-added blank slide, exactly like the inline
 * "Get started with layouts" strip does (suggested-layouts.tsx). Those paths
 * used to rely on the lingering `ai-slides.contentSlide.placement === "replace"`
 * that `addBlankSlide` sets — but the slides-library dialog wipes it back to the
 * "bottom" default on unmount (`dispatch(resetAiSlide())`), so a saved/theme
 * slide could insert as a NEW slide instead of replacing the blank one.
 *
 * This hook re-asserts the replace intent deterministically: if the active
 * thumbnail is still a "new" (unedited, just-added) slide — the same durable
 * signal SuggestedLayouts uses (`useNewSlidesStore.newSlides`) — target it for
 * replacement. Once the slide has been edited it is no longer "new", so a
 * library insert correctly adds a new slide instead.
 *
 * Returns a callback to invoke immediately before `handleAddSlides`.
 */
export const useEnsureReplaceNewSlide = () => {
  const dispatch = useAppDispatch();

  return useCallback(() => {
    // Read live: the caller runs this in the same tick as handleAddSlides.
    const activeThumbnailId = getThumbnailId(store.getState());
    if (!activeThumbnailId) return;
    const isNewSlide = Boolean(
      useNewSlidesStore.getState().newSlides[activeThumbnailId],
    );
    if (!isNewSlide) return;
    dispatch(
      setContentSlide({ id: activeThumbnailId, placement: "replace" }),
    );
  }, [dispatch]);
};
